import { NodeApi } from '@candulabs/craft';
import React, { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';

import { CRAFT_ROOT_ID } from 'craft/constants';
import { useTraversal } from 'craft/hooks/useSelection/useTraversal';
import { findAncestor } from 'craft/utils';

import { LayerContext } from './LayerContext';
import { initialInteractionState, InteractionType, reducer } from './reducer';

import { DragNode } from '../components/DragNode';
import {
  createDisplayableAreas,
  createErrorMessage,
  createLastChildNodes,
  isLastNodeOfParent,
} from '../utils';

export type InteractionLayerProps = {
  externalDraggedItems?: Set<string>;
  selectedNodes?: Set<string>;
  getNode: (nodeId: string) => NodeApi;
  onDragStart?: (nodeIds: string[]) => void;
  onDragEnd?: () => void;
  onSelectNode?: (nodeId: string[]) => void;
  onMoveNode?: (sourceNodeId: string[], parentNodeId: string, index: number) => void;
  nodeLayerCanvasRef: React.RefObject<HTMLDivElement>;
  children: React.ReactNode;
};

const AVG_ANIMATION_REFRESH_CYCLE = 16;

export const InteractionLayer: React.FC<InteractionLayerProps> = ({
  children,
  nodeLayerCanvasRef,
  getNode,
  onMoveNode,
  onSelectNode,
  externalDraggedItems,
  selectedNodes,
  onDragEnd,
  onDragStart,
}: InteractionLayerProps) => {
  const traversal = useTraversal();
  const allNodes = useMemo(() => traversal.descendants(CRAFT_ROOT_ID), []);

  const [state, dispatch] = useReducer(reducer, initialInteractionState);
  const preview = useRef<HTMLDivElement>(null);

  const thresholdRef = useRef<number>(0);
  const keyPressedRef = useRef<string>();

  const handleClick = useCallback(
    (event: MouseEvent) => {
      const isMultiSelect = event.metaKey || event.shiftKey;

      const hasNoActionAncestor = findAncestor(event.target as Element | null, `[data-dnd-id]`);

      const targetId = hasNoActionAncestor?.getAttribute('data-dnd-id');

      if (targetId && isMultiSelect) {
        const newSelectedNodeIds = [...state.selectedNodeIds];
        const indexOfTargetdNode = state.selectedNodeIds.indexOf(targetId);
        if (indexOfTargetdNode > -1) {
          newSelectedNodeIds.splice(indexOfTargetdNode, 1);
        } else {
          newSelectedNodeIds.push(targetId);
        }
        dispatch({
          type: InteractionType.Selected,
          payload: {
            selectedNodeIds: newSelectedNodeIds,
          },
        });
        onSelectNode?.(newSelectedNodeIds);
      } else if (targetId) {
        dispatch({
          type: InteractionType.Selected,
          payload: {
            selectedNodeIds: [targetId],
          },
        });
        onSelectNode?.([targetId]);
      }
    },
    [onSelectNode, state.selectedNodeIds, dispatch],
  );

  const handleDragEnd = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onDragEnd?.();
      dispatch({ type: InteractionType.DragEnd });
    },
    [onDragEnd, dispatch],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLElement> & DragEvent) => {
      event.stopPropagation();
      if (state?.targetedDropZone) {
        const [dropZoneNodeId, dropIntention] = state.targetedDropZone.split(`_`) as [
          string,
          'inside' | 'after',
        ];
        const dropRules = state?.dropZones[dropZoneNodeId];

        if (dropIntention === 'after' && dropRules?.canDropAfter === true) {
          const dropTargetNode = getNode(dropZoneNodeId).getParent();

          const newIndex = dropTargetNode.childNodes().indexOf(dropZoneNodeId);
          onMoveNode?.(state.draggedNodeIds, dropTargetNode.nodeId, newIndex + 1);
        } else if (dropIntention === 'inside' && dropRules?.canDropIn === true) {
          onMoveNode?.(state.draggedNodeIds, dropZoneNodeId, 0);
        }
      }
      handleDragEnd(event);
    },
    [onMoveNode, handleDragEnd, state?.targetedDropZone, state?.dropZones, getNode],
  );

  const handleDragEnter = useCallback(
    (event: React.DragEvent<HTMLElement> & DragEvent) => {
      const hasMovedOverThreshold = Date.now() > thresholdRef.current + AVG_ANIMATION_REFRESH_CYCLE;
      // Prevent default required to allow responding to drag and drop events
      event.preventDefault();
      if (!hasMovedOverThreshold) {
        return;
      }

      thresholdRef.current = Date.now();
      const draggedOverNodeId = (event.target as HTMLElement).getAttribute('data-dropzone-id');
      const draggedOverNodePosition = (event.target as HTMLElement).getAttribute(
        'data-dropzone-position',
      ) as 'inside' | 'after' | null;

      const targetedDropZone = `${draggedOverNodeId}_${draggedOverNodePosition}`;
      if (draggedOverNodeId && targetedDropZone !== state.targetedDropZone) {
        /**
         * Always target the inline DZ of a hovered node
         * If the nodeId referenced by the DZ does not have child nodes,
         *  then we also display the DZ "after"
         *  and, we also display any "after" DZ of its parents, provided they correpond to the last child of them
         */
        const hasChildNodes = getNode(draggedOverNodeId).childNodes().length;

        const childNodesArray =
          draggedOverNodePosition === 'after'
            ? createLastChildNodes(draggedOverNodeId, getNode, {
                [draggedOverNodeId]: { showDropAfter: true },
              })
            : { [draggedOverNodeId]: { showDropAfter: !hasChildNodes, showDropIn: true } };

        const hoveredDropZones =
          !hasChildNodes ||
          (draggedOverNodePosition === 'after' && isLastNodeOfParent(draggedOverNodeId, getNode))
            ? createDisplayableAreas(draggedOverNodeId, getNode, childNodesArray)
            : childNodesArray;

        dispatch({
          type: InteractionType.DragEnter,
          payload: {
            hoveredDropZones,
            targetedDropZone: `${draggedOverNodeId}_${draggedOverNodePosition}`,
          },
        });
      }
    },
    [state.targetedDropZone, dispatch],
  );

  const handleDragStart = useCallback(
    (event: React.DragEvent<HTMLDivElement> & DragEvent) => {
      event.stopPropagation();

      const draggedNodeIdsByTarget = (event.target as HTMLElement).getAttribute('data-dnd-id');

      const draggedNodeIds = state.selectedNodeIds.includes(draggedNodeIdsByTarget ?? '')
        ? state.selectedNodeIds
        : ([draggedNodeIdsByTarget] as string[]);

      if (
        !event.metaKey &&
        draggedNodeIds.every(
          (draggedNodeId) => draggedNodeId && getNode(draggedNodeId).isDraggable(),
        )
      ) {
        const svg = preview.current;
        if (svg) {
          event.dataTransfer?.setDragImage(svg, 150, 32);
        }

        const dropZones = Object.fromEntries(
          allNodes.map((nId, idx) => {
            const node = getNode(nId);

            return [
              nId,
              {
                canDropIn: createErrorMessage(node, draggedNodeIds, getNode),
                canDropAfter:
                  idx !== 0
                    ? createErrorMessage(node.getParent(), draggedNodeIds, getNode)
                    : 'Root Node',
              },
            ];
          }),
        );
        dispatch({
          type: InteractionType.DragStart,
          payload: {
            draggedNodeIds,
            dropZones,
          },
        });
        onDragStart?.(draggedNodeIds);
      } else {
        dispatch({
          type: InteractionType.DragEnd,
        });
        event.preventDefault();
      }
    },
    [getNode, state.selectedNodeIds, dispatch],
  );

  const handleFocus = useCallback(
    (e: React.FocusEvent) => {
      if (keyPressedRef.current !== 'Tab') {
        return;
      }
      const selectedNodeIds = [...(selectedNodes || [])];
      const focusedNodeId = (e.target as HTMLElement).getAttribute('data-dnd-id');
      if (focusedNodeId && (selectedNodeIds.length !== 1 || selectedNodeIds[0] !== focusedNodeId)) {
        onSelectNode?.([focusedNodeId]);
      }
    },
    [onSelectNode, selectedNodes, keyPressedRef.current],
  );

  // this is for storing the key pressed and ensure not double callback event is fired on click (because click event will trigger onClick and onFocus)
  const handleKeydown = useCallback((e: KeyboardEvent) => {
    keyPressedRef.current = e.code;
  }, []);
  const handleKeyup = useCallback(() => {
    keyPressedRef.current = undefined;
  }, []);

  useEffect(() => {
    const nodeLayerCanvas = nodeLayerCanvasRef.current;
    const preventDefault = (e: any) => {
      e.preventDefault();
      e.stopPropagation();
    };

    nodeLayerCanvas?.addEventListener('click', handleClick);
    document?.addEventListener('keydown', handleKeydown);
    document?.addEventListener('keyup', handleKeyup);

    nodeLayerCanvas?.addEventListener('dragenter', handleDragEnter);
    nodeLayerCanvas?.addEventListener('dragleave', preventDefault);

    document?.addEventListener('drop', handleDrop);

    document?.addEventListener('dragend', handleDragEnd);
    document?.addEventListener('dragover', preventDefault);

    return () => {
      nodeLayerCanvas?.removeEventListener('click', handleClick);

      nodeLayerCanvas?.removeEventListener('dragenter', handleDragEnter);
      nodeLayerCanvas?.removeEventListener('dragleave', preventDefault);

      document?.removeEventListener('keydown', handleKeydown);
      document?.removeEventListener('keyup', handleKeyup);

      document?.removeEventListener('drop', handleDrop);

      document?.removeEventListener('dragover', preventDefault);
      document?.removeEventListener('dragend', handleDragEnd);
    };
  }, [handleDragEnter, handleDrop, handleClick]);

  useEffect(() => {
    if (externalDraggedItems && externalDraggedItems.size > 0) {
      const items = [...externalDraggedItems];
      const dropZones = Object.fromEntries(
        allNodes.map((nId, idx) => {
          const node = getNode(nId);
          return [
            nId,
            {
              canDropIn: createErrorMessage(node, items, getNode),
              canDropAfter:
                idx !== 0 ? createErrorMessage(node.getParent(), items, getNode) : 'Root node',
            },
          ];
        }),
      );
      dispatch({
        type: InteractionType.DragStart,
        payload: {
          draggedNodeIds: items,
          dropZones,
        },
      });
    }
  }, [externalDraggedItems?.size, allNodes.length, getNode]);

  useEffect(() => {
    if (selectedNodes) {
      const selectedNodeIds = [...selectedNodes];
      dispatch({
        type: InteractionType.Selected,
        payload: {
          selectedNodeIds,
        },
      });
      if (selectedNodeIds.length === 1) {
        const nodeElements = nodeLayerCanvasRef.current?.querySelectorAll(
          `[data-dnd-id='${selectedNodeIds[0]}']`,
        );
        if (nodeElements?.[0]) {
          nodeElements?.[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
  }, [selectedNodes]);

  const context = useMemo(
    () => ({
      selectedNodeIds: state.selectedNodeIds,
      draggedNodeIds: state.draggedNodeIds,
      dropZones: state.dropZones,
      hoveredDropZones: state.hoveredDropZones,
      targetedDropZone: state.targetedDropZone,
      handleDragStart,
      handleFocus,
    }),
    [
      state.selectedNodeIds,
      state.draggedNodeIds,
      state.dropZones,
      state.hoveredDropZones,
      state.targetedDropZone,
      handleFocus,
    ],
  );

  const dragNodeTraits = useMemo(
    () =>
      state.draggedNodeIds.map((draggedNodeId) => {
        const data = getNode(draggedNodeId ?? '')?.get()?.data;
        return { nodeLabel: data?.name ?? '', isCanvas: data?.isCanvas };
      }),
    [state.draggedNodeIds],
  );

  return (
    <>
      <DragNode
        ref={preview}
        isDragging={!!state.draggedNodeIds.length}
        dragNodeTraits={dragNodeTraits}
      />
      <LayerContext.Provider value={context}>{children}</LayerContext.Provider>
    </>
  );
};
